import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Heart, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { cn } from '@/lib/utils'

const TABS = [
  { id: 'birthday', label: 'Birthday' },
  { id: 'due', label: 'Due visits' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'followup', label: 'Follow-up' },
]

/**
 * CRM retention hub — birthdays, due visits, inactive guests, follow-ups.
 */
export function CustomersRetentionPage() {
  const [tab, setTab] = useState('birthday')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [rows, setRows] = useState([])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      if (tab === 'birthday') {
        const { data } = await api.get('/reports/birthday-campaigns', {
          params: { days: 30, marketing_consent_only: 1 },
        })
        setRows(data?.data?.customers ?? data?.data?.rows ?? data?.data ?? [])
      } else if (tab === 'due') {
        const { data } = await api.get('/crm/retention/due-visits')
        setRows(data?.data?.due_visits ?? [])
      } else if (tab === 'inactive') {
        const { data } = await api.get('/crm/retention/inactive', { params: { days: 60 } })
        setRows(data?.data?.inactive ?? [])
      } else {
        const { data } = await api.get('/crm/retention/follow-ups')
        setRows(data?.data?.follow_ups ?? [])
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load this list.'))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [tab])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
      <header className="flex items-start gap-3">
        <Heart className="mt-1 h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold">Customer care</h1>
          <p className="text-sm text-muted-foreground">
            Birthdays, due visits, inactive guests, and post-service follow-ups.
          </p>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant="outline" size="sm">
          <Link to="/customers">Open customers</Link>
        </Button>
        {TABS.map((item) => (
          <Button
            key={item.id}
            type="button"
            size="sm"
            variant={tab === item.id ? 'default' : 'outline'}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </Button>
        ))}
      </div>

      <section className="rounded-2xl border border-border/70 bg-card p-4">
        <h2 className="mb-2 text-lg font-medium">
          {TABS.find((item) => item.id === tab)?.label}
        </h2>
        {loading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        ) : null}
        {error ? <p className="text-sm text-amber-800">{error}</p> : null}
        {!loading && !error && Array.isArray(rows) && rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing in this list right now.</p>
        ) : null}
        <ul className="divide-y divide-border/50 text-sm">
          {(Array.isArray(rows) ? rows : []).slice(0, 60).map((row, index) => (
            <li key={row.customer_id ?? row.id ?? index} className="py-2">
              <span className="font-medium">{row.name ?? row.customer_name ?? row.customer?.name ?? 'Guest'}</span>
              {row.phone || row.customer?.phone ? (
                <span className="text-muted-foreground"> · {row.phone ?? row.customer?.phone}</span>
              ) : null}
              {row.birthday || row.date_of_birth ? (
                <span className="text-muted-foreground"> · {row.birthday ?? row.date_of_birth}</span>
              ) : null}
              {row.due_on ? <span className="text-muted-foreground"> · due {row.due_on}</span> : null}
              {row.days_overdue != null ? (
                <span className="text-muted-foreground"> · {row.days_overdue}d overdue</span>
              ) : null}
              {row.inactive_days != null ? (
                <span className="text-muted-foreground"> · {row.inactive_days}d inactive</span>
              ) : null}
              {row.last_visit_at ? (
                <span className={cn('text-muted-foreground')}>
                  {' '}
                  · last {String(row.last_visit_at).slice(0, 10)}
                </span>
              ) : null}
              {row.completed_at || row.ends_at ? (
                <span className="text-muted-foreground">
                  {' '}
                  · visit {String(row.completed_at ?? row.ends_at).slice(0, 10)}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
